/**
 * CalendarGrid.jsx - the calendar itself: one row per engineer, one column per
 * day or week, with a project bar spanning each run.
 *
 * The Availability button in the header turns this grid inside out: booked
 * days go blank and the days with nothing on them become the bars, so who is
 * available from what day to what day reads straight off the board.
 *
 * A job block with no Project # is outlined in red (projectNumbers.js): the
 * engineer's My Schedule / My Project cannot show its GLOBAL #. `onlyMissing`
 * fades every other block so those stand out.
 *
 * Editable tables (10/16/26): `headerTools` sits in the header bar (the
 * utilization switch, up / down, delete), `rowTools(engineer)` beside each
 * name (the Move to menu), and `drop` makes the whole grid a place to drop a
 * dragged name: { onDragStart(engineer, ev), onDrop(ev), over, onOver(bool) }.
 * An empty table says how to fill it.
 */
import { useState } from 'react';
import { EMPTY_STYLE } from './manpowerConstants';
import { avatarInitials, fgFor } from './manpowerHelpers';
import { detailKey } from './manpowerAssignments';
import { fmtDate } from '../../utils/format';
import { MISSING_NUMBER_HINT, needsProjectNumber } from './projectNumbers';

const MISSING_RED = '#D32F2F';

/** The drag-and-drop type a dragged engineer name carries (its engineer id). */
export const ENGINEER_DRAG = 'application/x-globalops-engineer';

const FREE_STYLE = { bg: '#C8E6C9', fg: '#1B5E20', border: '1px solid #81C784' };

/** The tooltip lines for a block's details - only the fields it has. */
const detailLines = (d) =>
  d
    ? [
        d.status && `Status: ${d.status}`,
        d.location && `Location: ${d.location}`,
        d.projectNumber && `Project #: ${d.projectNumber}`,
      ].filter(Boolean)
    : [];

/**
 * Compute "runs" for one engineer over a sequence of column keys.
 * A run = ≥1 consecutive cells that share the same project_code AND the
 * same details. Cells with no project assignment stay one-day each (so
 * weekend gaps and coverage holes stay legible).
 *
 * Used in monthly view so an engineer on `P26006-020` from Mar 3 to
 * Mar 12 renders as ONE 10-day block. Weekends do NOT break runs. A
 * change of status or site does: one bar opens one editor, and saving it
 * would give both halves the first half's details.
 *
 * Returns an array of { startIdx, length, kind, code?, detail?, days? }.
 */
function buildRuns(columns, bucket) {
  const runs = [];
  let i = 0;
  while (i < columns.length) {
    const c = columns[i];
    const proj = bucket?.get(c.key);
    let len = 1;
    if (proj) {
      while (i + len < columns.length) {
        const nc = columns[i + len];
        const np = bucket?.get(nc.key);
        if (!np || np.code !== proj.code || detailKey(np.detail) !== detailKey(proj.detail)) break;
        len++;
      }
      runs.push({
        startIdx: i,
        length: len,
        kind: 'project',
        code: proj.code,
        detail: proj.detail,
        isWeekend: c.isWeekend,
        days: columns.slice(i, i + len),
      });
    } else {
      runs.push({ startIdx: i, length: 1, kind: 'empty', isWeekend: c.isWeekend, days: [c] });
    }
    i += len;
  }
  return runs;
}

export function CalendarGrid({
  title,
  engineers,
  columns,
  mode,
  assignments,
  freeAssignments,
  loaded,
  loadFailed,
  todayCol,
  colorFor,
  onProjectClick,
  onCellClick,
  onlyMissing = false,
  headerTools = null,
  rowTools = null,
  drop = null,
  testId,
}) {
  const colCount = columns.length;
  // Availability view on/off, per grid. It only takes effect once the shared
  // calendar has loaded: an empty schedule would read as everyone free.
  const [showFree, setShowFree] = useState(false);
  const free = showFree && !!loaded;
  // Column widths tuned per mode: monthly is narrow (28-31 daily cells),
  // weekly/quarterly are mid-width, yearly is wider (12 monthly columns).
  const colMinWidth = mode === 'monthly' ? 36 : mode === 'quarterly' ? 80 : mode === 'yearly' ? 100 : 100;
  // wider with a Move to menu beside each name, so the name keeps its room
  const engineerColWidth = rowTools ? 260 : 220;

  const dropHandlers = drop
    ? {
        onDragOver: (ev) => {
          if (!Array.from(ev.dataTransfer?.types || []).includes(ENGINEER_DRAG)) return;
          ev.preventDefault();
          ev.dataTransfer.dropEffect = 'move';
          if (!drop.over) drop.onOver(true);
        },
        onDragLeave: (ev) => {
          if (!ev.currentTarget.contains(ev.relatedTarget)) drop.onOver(false);
        },
        onDrop: (ev) => {
          ev.preventDefault();
          drop.onOver(false);
          drop.onDrop(ev);
        },
      }
    : {};

  return (
    <div
      data-testid={testId}
      {...dropHandlers}
      style={{
        background: '#fff',
        border: drop && drop.over ? '2px dashed #1B5E20' : '1px solid #E5E9DD',
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 24,
      }}
    >
      <div
        style={{
          background: '#0F3D14',
          color: '#fff',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.3 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {headerTools}
          <div style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.85 }}>
            {engineers.length} engineer{engineers.length === 1 ? '' : 's'}
          </div>
          <button
            type="button"
            aria-pressed={free}
            disabled={!loaded}
            onClick={() => setShowFree((v) => !v)}
            title={
              loaded
                ? free
                  ? 'Back to the projects'
                  : 'Show only the days each engineer has nothing on'
                : loadFailed
                  ? 'Could not load the calendar - reload the page to see who is available'
                  : 'Loading the calendar…'
            }
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              fontSize: 10.5,
              fontWeight: 800,
              fontFamily: 'Montserrat',
              letterSpacing: 0.3,
              cursor: loaded ? 'pointer' : loadFailed ? 'not-allowed' : 'wait',
              border: '1px solid rgba(255,255,255,0.55)',
              background: free ? '#fff' : 'transparent',
              color: free ? '#0F3D14' : '#fff',
              opacity: loaded ? 1 : 0.5,
            }}
          >
            {free ? '✓ Availability' : 'Availability'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: engineerColWidth + colMinWidth * colCount }}>
          {/* Header row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `${engineerColWidth}px repeat(${colCount}, minmax(${colMinWidth}px, 1fr))`,
              background: '#F5F8F0',
              borderBottom: '1px solid #E5E9DD',
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 1.4,
                color: '#666',
                textTransform: 'uppercase',
                borderRight: '1px solid #E5E9DD',
              }}
            >
              Engineer
            </div>
            {columns.map((c) => {
              const isNow = c.key === todayCol;
              return (
                <div
                  key={c.key}
                  style={{
                    padding: mode === 'monthly' ? '6px 2px' : '10px 8px',
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: 1.1,
                    textTransform: 'uppercase',
                    color: isNow ? '#1B5E20' : c.isWeekend ? '#BBB' : '#666',
                    background: isNow ? '#E8F5E9' : c.isWeekend ? '#FAFBF7' : 'transparent',
                    borderLeft: isNow ? '2px solid #1B5E20' : '1px solid #F0F2EC',
                    borderRight: isNow ? '2px solid #1B5E20' : 'none',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: mode === 'monthly' ? 11 : 9.5, fontWeight: 800 }}>{c.label}</div>
                  {c.sublabel && <div style={{ fontSize: 9.5, fontWeight: 600, opacity: 0.7, marginTop: 1 }}>{c.sublabel}</div>}
                </div>
              );
            })}
          </div>

          {/* Engineer rows */}
          {engineers.map((e) => {
            const bucket = (free ? freeAssignments : assignments)?.get(e.name);
            // Merge consecutive same-project cells in monthly, quarterly,
            // and yearly modes (so a 4-week stint on KSA reads as one block,
            // and so a 3-month project run merges into a single yearly cell).
            // Weekly stays per-cell since the 8-week sliding window already
            // shows discrete movement week by week. Free time merges by day
            // only: a week or month column says how many of ITS days are
            // free, and merging those would hide a busy week in between.
            const runs =
              mode === 'monthly' || (!free && (mode === 'quarterly' || mode === 'yearly'))
                ? buildRuns(columns, bucket)
                : columns.map((c, idx) => {
                    const proj = bucket?.get(c.key);
                    return proj
                      ? {
                          startIdx: idx,
                          length: 1,
                          kind: 'project',
                          code: proj.code,
                          detail: proj.detail,
                          isWeekend: c.isWeekend,
                          days: [c],
                        }
                      : { startIdx: idx, length: 1, kind: 'empty', isWeekend: c.isWeekend, days: [c] };
                  });

            return (
              <div
                key={e.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `${engineerColWidth}px repeat(${colCount}, minmax(${colMinWidth}px, 1fr))`,
                  borderBottom: '1px solid #F4F6F0',
                  minHeight: 50,
                }}
              >
                <div
                  draggable={drop ? true : undefined}
                  onDragStart={drop ? (ev) => drop.onDragStart(e, ev) : undefined}
                  title={drop ? 'Drag onto another table to move, or use Move to' : undefined}
                  style={{
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#FAFBF7',
                    borderRight: '1px solid #E5E9DD',
                    cursor: drop ? 'grab' : undefined,
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: '#1B5E20',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10.5,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {avatarInitials(e.name)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: '#0D0D0D',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e.name}
                    </div>
                    {/* Whatever the engineer's card says, verbatim - no code
                        translated into a caption the sheet chose. Blank when
                        the card's Skill is blank. */}
                    {e.subtitle && (
                      <div
                        style={{
                          fontSize: 9,
                          color: '#888',
                          fontWeight: 600,
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {e.subtitle}
                      </div>
                    )}
                  </div>
                  {rowTools && rowTools(e)}
                </div>

                {runs.map((run, ri) => {
                  const containsToday = todayCol && run.days?.some((c) => c.key === todayCol);
                  const slot = {
                    gridColumn: `${2 + run.startIdx} / span ${run.length}`,
                    background: containsToday ? 'rgba(76,175,80,0.04)' : 'transparent',
                    borderLeft: containsToday ? '2px solid #1B5E20' : 'none',
                    borderRight: containsToday ? '2px solid #1B5E20' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  };
                  if (free) {
                    // Here a "project" run is free time. One with no free
                    // weekday in it (a lone free weekend) stays blank.
                    const freeDays = run.kind === 'project' ? run.days.reduce((t, c) => t + (bucket?.get(c.key)?.days || 0), 0) : 0;
                    const weekdays = run.days.filter((c) => !c.isWeekend);
                    const first = weekdays[0] || run.days[0];
                    const last = weekdays[weekdays.length - 1] || run.days[run.days.length - 1];
                    const from = first.from || first.key;
                    const to = last.to || last.key;
                    // A day stretch is wholly free, so it can be booked in one
                    // go. A week or month cell can be partly busy: no click,
                    // and its tooltip lists the free days rather than its span.
                    const bookable = freeDays > 0 && mode === 'monthly';
                    const when =
                      mode === 'monthly'
                        ? `${fmtDate(from)} → ${fmtDate(to)}`
                        : (bucket?.get(run.days[0].key)?.ranges || [])
                            .map((r) => (r.from === r.to ? fmtDate(r.from) : `${fmtDate(r.from)} → ${fmtDate(r.to)}`))
                            .join(', ');
                    return (
                      <div key={ri} style={{ ...slot, padding: mode === 'monthly' ? (run.length > 1 ? '3px 0' : '3px 1px') : 6 }}>
                        {freeDays > 0 && (
                          <div
                            title={`Available ${freeDays} weekday${freeDays === 1 ? '' : 's'} · ${when}${bookable ? '\nClick to put them on a project for these days' : ''}`}
                            onClick={
                              bookable
                                ? () => onCellClick({ engineerName: e.rawName || e.name, dateFrom: from, dateTo: to, initialCode: '' })
                                : undefined
                            }
                            style={{
                              width: '100%',
                              height: mode === 'monthly' ? 34 : 36,
                              borderRadius: 5,
                              background: FREE_STYLE.bg,
                              color: FREE_STYLE.fg,
                              border: FREE_STYLE.border,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: mode === 'monthly' && run.length === 1 ? 8.5 : 10.5,
                              fontWeight: 800,
                              cursor: bookable ? 'pointer' : 'default',
                              userSelect: 'none',
                              overflow: 'hidden',
                              padding: '0 6px',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {mode === 'monthly' ? (run.length >= 3 ? `Available · ${freeDays}d` : 'Available') : `${freeDays}d available`}
                          </div>
                        )}
                      </div>
                    );
                  }
                  const isProject = run.kind === 'project';
                  const isMerged = mode === 'monthly' && run.length > 1;
                  const cellBg = isProject ? colorFor(run.code) : EMPTY_STYLE.bg;
                  const cellFg = isProject ? fgFor(cellBg) : '#BBB';
                  const cellLabel = isProject ? (mode === 'monthly' && run.length >= 3 ? `${run.code} · ${run.length}d` : run.code) : '';
                  const missing = isProject && needsProjectNumber(run.code, run.detail);
                  const faded = onlyMissing && isProject && !missing;
                  const tooltip = isProject
                    ? [
                        `${run.code} · ${run.length} day${run.length > 1 ? 's' : ''}`,
                        ...detailLines(run.detail),
                        ...(missing ? [`\u26a0 ${MISSING_NUMBER_HINT}`] : []),
                      ].join('\n')
                    : '';
                  // The column's real date span. `key` is only a date in
                  // monthly mode - weekly/quarterly key on "2026-W38" and
                  // yearly on "2026-09", which the editor cannot parse.
                  const firstDay = run.days[0]?.from || run.days[0]?.key;
                  const lastDay = run.days[run.days.length - 1]?.to || run.days[run.days.length - 1]?.key;
                  const handleClick = (ev) => {
                    // Alt+click on a project block jumps to its color editor;
                    // plain click opens the assignment editor.
                    if (isProject && (ev.altKey || ev.metaKey)) {
                      onProjectClick(run.code);
                    } else {
                      onCellClick({
                        engineerName: e.rawName || e.name,
                        dateFrom: firstDay,
                        // A week or month column covers a real range, so fill
                        // the whole thing; a single day only covers itself.
                        dateTo: lastDay,
                        initialCode: isProject ? run.code : '',
                        // What the block really covers, for clearing it on a
                        // move: week columns run through Sunday (spanEnd).
                        clearFrom: firstDay,
                        clearTo: run.days[run.days.length - 1]?.spanEnd || lastDay,
                      });
                    }
                  };
                  return (
                    <div
                      key={ri}
                      style={{
                        gridColumn: `${2 + run.startIdx} / span ${run.length}`,
                        padding: isMerged ? '3px 0' : mode === 'monthly' ? '3px 1px' : 6,
                        background: containsToday ? 'rgba(76,175,80,0.04)' : 'transparent',
                        borderLeft: containsToday ? '2px solid #1B5E20' : 'none',
                        borderRight: containsToday ? '2px solid #1B5E20' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        title={`${tooltip}${isProject ? '\nClick to edit · Alt-click to recolor' : '\nClick to add an assignment'}`}
                        data-missing-number={missing ? 'true' : undefined}
                        onClick={handleClick}
                        style={{
                          width: '100%',
                          height: mode === 'monthly' ? 34 : 36,
                          borderRadius: isMerged ? 4 : 5,
                          // flagged: a red corner on top of the project colour,
                          // inside the red outline - no extra text, so the
                          // label keeps all the room it had
                          background: missing ? `linear-gradient(225deg, ${MISSING_RED} 0 9px, transparent 9px), ${cellBg}` : cellBg,
                          color: cellFg,
                          border: isProject ? 'none' : EMPTY_STYLE.border,
                          // inset, so a one-day block keeps its width
                          outline: missing ? `2px solid ${MISSING_RED}` : 'none',
                          outlineOffset: missing ? -2 : 0,
                          opacity: faded ? 0.25 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: mode === 'monthly' ? (run.length > 1 ? 10.5 : 8.5) : 10.5,
                          fontWeight: 700,
                          letterSpacing: 0.2,
                          cursor: 'pointer',
                          userSelect: 'none',
                          overflow: 'hidden',
                          boxShadow: missing
                            ? 'inset 0 0 0 3px #fff, 0 1px 2px rgba(0,0,0,0.1)'
                            : isProject
                              ? '0 1px 2px rgba(0,0,0,0.1)'
                              : 'none',
                          padding: '0 6px',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          transition: 'transform 0.1s ease',
                        }}
                        onMouseEnter={(ev) => {
                          ev.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(ev) => {
                          ev.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {cellLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {engineers.length === 0 && (
            <div style={{ padding: '16px 14px', fontSize: 11, color: '#888', fontWeight: 600 }}>
              {drop ? 'No engineers in this table yet - drag a name here, or use "Move to" beside a name.' : 'No engineers in this table.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
